import React, { useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { supplyChainService } from '../services/apiClient';

const PRODUCT_COLORS: { [key: string]: string } = {
  'NHH': '#3b82f6',  // blue
  'ADRQ Series': '#10b981',  // emerald
  'ARP Series': '#f59e0b',  // amber
  'ARR Series': '#8b5cf6',  // violet
  'RG Series': '#ef4444',  // red
  'Marketing Materials': '#06b6d4',  // cyan
  'Other Products': '#64748b',  // slate
};

const FALLBACK_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#64748b', '#f97316'
];

interface ProductMixChartProps {
  className?: string;
}

export const ProductMixChart: React.FC<ProductMixChartProps> = ({ className = '' }) => {
  const [period, setPeriod] = useState<30 | 60 | 90 | 180>(90);
  const [viewMode, setViewMode] = useState<'percentage' | 'quantity'>('percentage');

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['productMix', period],
    queryFn: async () => {
      const response = await supplyChainService.getProductMix(period);
      return response.data;
    },
    refetchInterval: 300000,
    staleTime: 240000,
  });

  const getColor = (product: string, index: number): string =>
    PRODUCT_COLORS[product] ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length];

  if (isLoading) {
    return (
      <div className={`bg-white rounded-2xl shadow-sm p-6 ${className}`}>
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-gray-200 rounded w-1/3" />
          <div className="h-96 bg-gray-100 rounded-lg" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={`bg-white rounded-2xl shadow-sm p-6 ${className}`}>
        <div className="flex items-center justify-center space-x-3 text-red-600 h-64">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm">Error loading data: {error instanceof Error ? error.message : 'Unknown error'}</span>
          <button onClick={() => refetch()} className="text-blue-600 hover:text-blue-800 underline text-sm">Retry</button>
        </div>
      </div>
    );
  }

  if (!data || !data.time_series || data.time_series.length === 0) {
    return (
      <div className={`bg-white rounded-2xl shadow-sm p-6 ${className}`}>
        {/* Controls */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex space-x-2">
            {[30, 60, 90, 180].map((days) => (
              <button
                key={days}
                onClick={() => setPeriod(days as 30 | 60 | 90 | 180)}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${period === days ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                {days} Days
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col items-center justify-center space-y-4 text-gray-500 h-64 border-2 border-dashed border-gray-200 rounded-lg">
          <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <p className="text-sm text-gray-500">No sales data found in the last {period} days.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ── Chart data ────────────────────────────────────────────────────────────
  const chartData = data.time_series.map((week: any) => {
    const weekData: any = {
      week: new Date(week.week).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    };
    Object.keys(week.products).forEach((product: string) => {
      weekData[product] = viewMode === 'percentage'
        ? week.products[product].percentage
        : week.products[product].quantity;
    });
    return weekData;
  });

  // ── Trend calculations ────────────────────────────────────────────────────
  const allTrends = Object.entries(data.trends || {});

  let trendingUp = allTrends
    .filter(([_, t]: [string, any]) => t.trend === 'up')
    .sort((a, b) => (b[1] as any).change - (a[1] as any).change)
    .slice(0, 3);

  let trendingDown = allTrends
    .filter(([_, t]: [string, any]) => t.trend === 'down')
    .sort((a, b) => (a[1] as any).change - (b[1] as any).change)
    .slice(0, 3);

  if (trendingUp.length === 0 && allTrends.length > 0) {
    trendingUp = allTrends.filter(([_, t]: [string, any]) => t.change > 0)
      .sort((a, b) => (b[1] as any).change - (a[1] as any).change).slice(0, 2);
  }
  if (trendingDown.length === 0 && allTrends.length > 0) {
    trendingDown = allTrends.filter(([_, t]: [string, any]) => t.change < 0)
      .sort((a, b) => (a[1] as any).change - (b[1] as any).change).slice(0, 2);
  }

  // ── Stat card data ────────────────────────────────────────────────────────
  const topProduct = allTrends.length > 0
    ? allTrends.sort((a, b) => (b[1] as any).end_percentage - (a[1] as any).end_percentage)[0]
    : null;

  const biggestGainer = trendingUp.length > 0 ? trendingUp[0] : null;
  const biggestLoser = trendingDown.length > 0 ? trendingDown[0] : null;
  const weeksOfData = data.time_series.length;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={`bg-white rounded-2xl shadow-sm ${className}`}>
      {/* Controls */}
      <div className="px-6 pt-6 pb-4 flex items-center justify-between">
        <div className="flex space-x-2">
          {[30, 60, 90, 180].map((days) => (
            <button
              key={days}
              onClick={() => setPeriod(days as 30 | 60 | 90 | 180)}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${period === days ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {days} Days
            </button>
          ))}
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => setViewMode('percentage')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'percentage' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
          >
            Percentage
          </button>
          <button
            onClick={() => setViewMode('quantity')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'quantity' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
          >
            Quantity
          </button>
        </div>
      </div>

      {/* Chart — exact StockGraphModal style */}
      <div className="px-4 pb-4">
        <ResponsiveContainer width="100%" height={400}>
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <defs>
              {data.products.map((product: string, index: number) => {
                const color = getColor(product, index);
                const id = `grad-${product.replace(/\s+/g, '-')}`;
                return (
                  <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color} stopOpacity={0.8} />
                    <stop offset="95%" stopColor={color} stopOpacity={0.1} />
                  </linearGradient>
                );
              })}
            </defs>

            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />

            <XAxis
              dataKey="week"
              stroke="#6b7280"
              tick={{ fontSize: 12 }}
            />

            <YAxis
              stroke="#6b7280"
              tick={{ fontSize: 12 }}
              domain={viewMode === 'percentage' ? [0, 100] : undefined}
              tickFormatter={(v) => viewMode === 'percentage' ? `${v}%` : v.toLocaleString()}
            />

            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px'
              }}
              formatter={(value: any, name: string) => [
                viewMode === 'percentage'
                  ? `${Number(value).toFixed(1)}%`
                  : `${Number(value).toLocaleString()} units`,
                name
              ]}
            />

            <Legend
              wrapperStyle={{ fontSize: '12px', paddingTop: '16px' }}
              iconType="circle"
            />

            {data.products.map((product: string, index: number) => (
              <Area
                key={product}
                type="monotone"
                dataKey={product}
                stackId="1"
                stroke={getColor(product, index)}
                fill={`url(#grad-${product.replace(/\s+/g, '-')})`}
                strokeWidth={2}
                name={product}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Stat cards — same pattern as StockGraphModal */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 px-6 pb-6">
        {/* Top Product */}
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">Top Product</div>
          <div className="text-xl font-bold text-blue-900 leading-tight">
            {topProduct ? topProduct[0] : '—'}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {topProduct ? `${(topProduct[1] as any).end_percentage.toFixed(1)}% of sales` : 'No data'}
          </div>
        </div>

        {/* Biggest Gainer */}
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">Biggest Gainer</div>
          <div className="text-xl font-bold text-green-900 leading-tight">
            {biggestGainer ? biggestGainer[0] : '—'}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {biggestGainer
              ? `+${(biggestGainer[1] as any).change.toFixed(1)}% over ${period}d`
              : 'No gainers'}
          </div>
        </div>

        {/* Biggest Loser */}
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">Biggest Decline</div>
          <div className="text-xl font-bold text-purple-900 leading-tight">
            {biggestLoser ? biggestLoser[0] : '—'}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {biggestLoser
              ? `${(biggestLoser[1] as any).change.toFixed(1)}% over ${period}d`
              : 'No declines'}
          </div>
        </div>

        {/* Data Coverage */}
        <div className="bg-orange-50 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">Data Coverage</div>
          <div className="text-xl font-bold text-orange-900">{weeksOfData}</div>
          <div className="text-xs text-gray-500 mt-1">
            weeks of data ({data.products.length} products)
          </div>
        </div>
      </div>

      {/* Limited data warning */}
      {data.time_series.length < 4 && data.time_series.length > 0 && (
        <div className="mx-6 mb-6 text-center text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
          ⚠️ Limited data ({data.time_series.length} week{data.time_series.length !== 1 ? 's' : ''}).
          Trends may not be significant — try a longer period.
        </div>
      )}
    </div>
  );
};

export default ProductMixChart;
